// File: src/components/LocationField.jsx

import React, { useEffect, useRef, useState } from 'react';
import { LuMapPin } from 'react-icons/lu';

import MarketSelectionModal from './MarketSelectionModal';

const MARKET_KEY = 'tribe_selected_market';
const MARKET_EVENT = 'tribe:market-selected';

function getMarketId(value) {
  return String(value?._id ?? value ?? '');
}

function readSavedMarketId() {
  try {
    const saved = JSON.parse(
      localStorage.getItem(MARKET_KEY) || 'null'
    );

    return getMarketId(saved?.marketId);
  } catch {
    return '';
  }
}

export default function LocationField({
  onMarketSelected,
  onLocationSelected,
  selectedMarketId,
  compactMobile = false,
}) {
  const [markets, setMarkets] = useState([]);
  const [localMarketId, setLocalMarketId] = useState(
    readSavedMarketId
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [focused, setFocused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [screenWidth, setScreenWidth] = useState(() =>
    typeof window === 'undefined' ? 1024 : window.innerWidth
  );

  const callbackRef = useRef(null);
  const controlledIdRef = useRef(selectedMarketId);

  const isControlled = selectedMarketId !== undefined;

  const marketId = isControlled
    ? getMarketId(selectedMarketId)
    : localMarketId;

  const isMobile = screenWidth <= 768;
  const isTightMobile = compactMobile && screenWidth <= 440;

  useEffect(() => {
    callbackRef.current =
      onMarketSelected || onLocationSelected;

    controlledIdRef.current = selectedMarketId;
  }, [
    onMarketSelected,
    onLocationSelected,
    selectedMarketId,
  ]);

  useEffect(() => {
    function handleResize() {
      setScreenWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

useEffect(() => {
  const controller = new AbortController();
  let active = true;

  async function loadMarkets() {
    setLoading(true);
    setError('');

    try {
      const apiUrl = (
        process.env.REACT_APP_API_URL || ''
      ).replace(/\/+$/, '');

      const response = await fetch(
        `${apiUrl}/events/markets`,
        { signal: controller.signal }
      );

      if (!response.ok) {
        throw new Error('Unable to load cities.');
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error('Unable to load cities.');
      }

      const availableMarkets = data
        .filter(
          (market) =>
            market?._id &&
            typeof market.name === 'string' &&
            market.name.trim()
        )
        .map((market) => ({
          ...market,
          _id: String(market._id),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      if (!active) return;

      setMarkets(availableMarkets);

      // Preserve an existing selection supplied by the parent.
      if (getMarketId(controlledIdRef.current)) return;

      const savedId = readSavedMarketId();

      // Use the saved selection, otherwise default to Los Angeles.
      const selected =
        availableMarkets.find((market) => market._id === savedId) ||
        availableMarkets.find(
          (market) =>
            market.name.trim().toLowerCase() === 'los angeles'
        );

      setLocalMarketId(selected?._id || '');

      if (selected) {
        const selection = {
          marketId: selected._id,
          label: selected.name,
          market: selected,
        };

        try {
          localStorage.setItem(
            MARKET_KEY,
            JSON.stringify({
              marketId: selected._id,
              label: selected.name,
            })
          );
        } catch {
          // Browsing still works without local storage.
        }

        window.dispatchEvent(
          new CustomEvent(MARKET_EVENT, {
            detail: selection,
          })
        );

        callbackRef.current?.(selection);
      } else if (savedId) {
        try {
          localStorage.removeItem(MARKET_KEY);
        } catch {
          // Browsing still works without local storage.
        }
      }
    } catch (err) {
      if (!active || err.name === 'AbortError') return;

      setError('Unable to load cities.');
    } finally {
      if (active) setLoading(false);
    }
  }

  loadMarkets();

  return () => {
    active = false;
    controller.abort();
  };
}, [retryCount]);

  useEffect(() => {
    function handleExternalSelection(event) {
      const nextId = getMarketId(event.detail?.marketId);

      setLocalMarketId(nextId);
    }

    function handleStorageChange(event) {
      if (event.key === MARKET_KEY || event.key === null) {
        setLocalMarketId(readSavedMarketId());
      }
    }

    window.addEventListener(
      MARKET_EVENT,
      handleExternalSelection
    );
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(
        MARKET_EVENT,
        handleExternalSelection
      );
      window.removeEventListener(
        'storage',
        handleStorageChange
      );
    };
  }, []);

  function handleSelect(nextMarketId) {
    const selected = markets.find(
      (market) => market._id === nextMarketId
    );

    if (!selected) return;

    const selection = {
      marketId: selected._id,
      label: selected.name,
      market: selected,
    };

    setLocalMarketId(selected._id);

    try {
      localStorage.setItem(
        MARKET_KEY,
        JSON.stringify({
          marketId: selected._id,
          label: selected.name,
        })
      );
    } catch {
      // Browsing still works without local storage.
    }

    window.dispatchEvent(
      new CustomEvent(MARKET_EVENT, {
        detail: selection,
      })
    );

    callbackRef.current?.(selection);
  }

  const selectedMarket = markets.find(
    (market) => market._id === marketId
  );

  const placeholder = loading
    ? 'Loading cities…'
    : error
      ? 'Cities unavailable'
      : markets.length === 0
        ? 'No cities available'
        : 'Choose a city';

  const cityName = loading || error
    ? placeholder
    : selectedMarket?.name || placeholder;
  const regionLabel = !loading && !error && selectedMarket?.name === 'Los Angeles'
    ? ', CA, USA'
    : '';

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: isMobile ? '100%' : 260, maxWidth: '100%',
        flex: isMobile ? '1 1 0' : '0 1 auto', minWidth: 0,
        height: isMobile ? 32 : 40, background: 'transparent',
      }}>
        <button type="button" aria-haspopup="dialog" aria-expanded={pickerOpen}
          aria-label={`Choose your city. ${cityName}${regionLabel}`}
          onClick={() => setPickerOpen(true)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            maxWidth: '100%', minWidth: 0, height: '100%', padding: 0,
            border: 0, borderRadius: 4, background: 'transparent', color: '#000',
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            fontSize: isTightMobile ? 14 : isMobile ? 14 : 15,
            fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2,
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}>
          <LuMapPin aria-hidden="true" style={{ fontSize: 16, flexShrink: 0, marginRight: 8 }} />
          <span aria-hidden="true" style={{
            display: 'flex', alignItems: 'baseline', minWidth: 0, overflow: 'hidden',
          }}>
            <span style={{
              flex: '0 1 auto', minWidth: 0, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{cityName}</span>
            {regionLabel && <span style={{
              flex: '0 1000 auto', minWidth: 0, whiteSpace: 'pre',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{regionLabel}</span>}
          </span>
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none"
            style={{ flexShrink: 0, marginLeft: 8 }}>
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <MarketSelectionModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        markets={markets}
        selectedMarketId={marketId}
        onSelect={handleSelect}
        loading={loading}
        error={error}
        onRetry={() => setRetryCount((count) => count + 1)}
      />
    </>
  );
}
